import { ICON_COLOR } from '@zep-us/zus/tokens';
import { GlobeFillIcon, LockFillIcon } from '@zep-us/zus-icons';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { useFormatLargeNumber } from '@/hooks/number/useLargeNumber';
import { PlusIcon } from '@/icons';
import { useCreateSubjectNameGetter } from '@/shared/category/useCreateSubjectNameGetter';
import { useCachedCategoryInfo } from '@/shared/category/useFetchCategoryInfo';
import { EmptyListState } from '@/shared/components/itemList/EmptyListState';
import { InfiniteListView } from '@/shared/components/itemList/InfiniteListView';
import { type Folder, type FolderVisibility, useAddQuizFlow, useFolderItems, useIsFolderOwner } from '@/shared/features/quiz-folder';
import { useGetQuizListItemButtons } from '@/shared/quizList/hooks';
import { QuizListItem } from '@/shared/quizList/QuizListItem';
import { Button } from '@/ui_v3';
interface FolderQuizSectionProps {
  folder: Folder;
}

/** 머리말의 공개 상태 표기. 퀴즈 상세와 같은 아이콘을 쓴다. */
const VISIBILITY_PRESENTATION = {
  PUBLIC: {
    Icon: GlobeFillIcon,
    labelKey: 'common:folder.detail.visibility.public'
  },
  PRIVATE: {
    Icon: LockFillIcon,
    labelKey: 'common:folder.detail.visibility.private'
  }
} as const satisfies Record<FolderVisibility, {
  Icon: typeof GlobeFillIcon;
  labelKey: string;
}>;

/**
 * 담긴 퀴즈가 없을 때. 액션은 소유자에게만 보인다.
 *
 * `내 퀴즈에서 추가` 는 편집 화면으로 보내지 않고 이 자리에서 퀴즈 추가 모달을 연다.
 */
const FolderEmptyQuizzes = ({
  folderId,
  isOwner
}: {
  folderId: string;
  isOwner: boolean;
}) => {
  const {
    t
  } = useTranslation(['common']);
  const router = useRouter();
  const {
    openAddQuizPicker
  } = useAddQuizFlow(folderId);
  return <EmptyListState title={t('common:folder.detail.empty.title')} description={t('common:folder.detail.empty.description')} image={
  // 벡터와 텍스처 이미지가 섞인 일러스트라 통째로 내보낸 PNG 를 쓴다.
  // 높이는 원본 비율(126:131)을 지킨다.
  <img className={'h-[131px] w-[126px] shrink-0'} src={'/assets/folder/empty-quizzes.png'} alt={''} aria-hidden />}
  // 남의 폴더에서는 액션이 없다.
  action={isOwner && <div className={'flex items-start justify-center gap-2xs'}>
            <Button variant={'outlined'} intent={'neutral'} size={'lg'} rounded={'full'} onClick={() => void router.push('/public')}>
              {t('common:folder.detail.empty.findInLibrary')}
            </Button>
            <Button variant={'outlined'} intent={'alternative'} size={'lg'} rounded={'full'}
    // 빈 폴더라 제외할 퀴즈가 없다.
    onClick={() => void openAddQuizPicker([])}>
              <PlusIcon size={'md'} />
              {t('common:folder.detail.empty.addFromMyQuiz')}
            </Button>
          </div>} data-sentry-element="EmptyListState" data-sentry-component="FolderEmptyQuizzes" data-sentry-source-file="FolderQuizSection.tsx" />;
};

/**
 * 담긴 퀴즈 목록.
 *
 * 머리말의 개수는 `items.length` 가 아니라 서버가 준 `quizsetCount` 를 쓴다.
 * 상세는 20개씩 끊겨 온다. 담긴 순서가 곧 재생 순서라 이어 받은 페이지를 다시 정렬하지 않는다.
 */
export const FolderQuizSection = ({
  folder
}: FolderQuizSectionProps) => {
  const {
    t
  } = useTranslation(['common']);
  const formatLargeNumber = useFormatLargeNumber();
  const isOwner = useIsFolderOwner(folder.ownerId);
  // 소유자에게는 내 퀴즈 목록과 같은 메뉴를 준다.
  // 남이 보는 폴더에는 `내 퀴즈로 저장` 하나만 둔다.
  const getQuizButtons = useGetQuizListItemButtons(isOwner ? 'myQuiz' : 'folder');
  const {
    items,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage
  } = useFolderItems(folder);
  // 수정 화면에 들어가지 않고 이 자리에서 담는다.
  const {
    openAddQuizPicker
  } = useAddQuizFlow(folder.id);
  const {
    categoryTreeData
  } = useCachedCategoryInfo();
  const subjectNameGetter = useCreateSubjectNameGetter(categoryTreeData?.subjectTree);
  const visibility = VISIBILITY_PRESENTATION[folder.visibility];
  return <section className={'flex w-full flex-col'} data-sentry-component="FolderQuizSection" data-sentry-source-file="FolderQuizSection.tsx">
      <header className={'flex items-center gap-xs px-4xs py-sm'}>
        <div className={'flex min-w-px flex-1 flex-col gap-3xs'}>
          <h2 className={'text-body-lg-bold text-default'}>
            {t('common:folder.card.quizCount', {
            quizCount: formatLargeNumber(folder.quizsetCount)
          })}
          </h2>
          <div className={'flex shrink-0 items-center gap-2xs'}>
            <div className={'flex size-[16px] items-center justify-center'}>
              <visibility.Icon size={'2xs'} fill={ICON_COLOR.neutral} data-sentry-element="unknown" data-sentry-source-file="FolderQuizSection.tsx" />
            </div>
            <p className={'text-caption-lg-semibold text-neutral'}>
              {t(visibility.labelKey)}
            </p>
          </div>
        </div>
        {/* 빈 폴더는 빈 상태의 액션이 같은 일을 한다. */}
        {isOwner && folder.quizsetCount > 0 && <Button variant={'outlined'} intent={'neutral'} size={'md'} leftSlot={<PlusIcon size={'sm'} />} onClick={() => void openAddQuizPicker(items.map(item => item.quizsetId))}>
            {t('common:folder.edit.addQuiz')}
          </Button>}
      </header>

      {/* 첫 페이지는 SSR 로 이미 있어 로딩·오류 상태가 없다. 이어 받기만 골격에 맡긴다. */}
      <InfiniteListView items={items} isLoading={false} isError={false} hasNextPage={!!hasNextPage} fetchNextPage={() => void fetchNextPage()} isFetchingNextPage={isFetchingNextPage} gridClassName={'flex w-full flex-col'} className={'pb-none'} EmptyComponent={<FolderEmptyQuizzes folderId={folder.id} isOwner={isOwner} />} renderItem={({
      quizsetId,
      quizset
    }) => <QuizListItem key={quizsetId} eventLocation={'folder'}
    // 폴더 안에서 난 행동을 폴더별·소유자별로 나눠 볼 수 있게 한다.
    eventAdditionalProperty={{
      folder: {
        folder_id: folder.id,
        is_folder_owner: isOwner
      }
    }}
    // 자료실과 같이 작성자를 항상 그린다. 소유자 판정은 클라이언트에서만 가능해
    // SSR HTML 과 달라지면 수화 뒤 작성자 줄이 사라지며 레이아웃이 밀린다.
    isHideAuthor={false} alwaysDropDownMenu={isOwner} subjectName={subjectNameGetter({
      subjectGroupId: quizset.categoryInfo?.subject,
      subSubjectGroupId: quizset.categoryInfo?.subSubject
    })} menuItems={getQuizButtons(quizset)}
    // 행에서 재생을 시작하면 이 폴더를 이어 간다. 개인 '다음 퀴즈'보다 폴더가 우선이다.
    folderPlayOrigin={folder.id} quizSet={quizset} />} data-sentry-element="InfiniteListView" data-sentry-source-file="FolderQuizSection.tsx" />
    </section>;
};